import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { useSetSearchParams } from "@src/appV2/lib/utils/useSetSearchParams";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { type transformResponseIntoInterview } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useEffect } from "react";

import { getPlacementDetailsPath } from "../../paths";
import { applicationActiveSectionStorageKey, showApplicationsStorageKey } from "../../utils";
import { InterviewPlacementCard } from "../AppliedPlacementCard/InterviewPlacementCard";
import { PlacementsAccordionSectionV3 } from "../PlacementAccordionSection/PlacementsAccordionSectionV3";

interface InterviewsAccordionSectionProps {
  interviews: Array<ReturnType<typeof transformResponseIntoInterview>>;
  isLoadingInterviews: boolean;
  hasNextPageInterviews: boolean;
  isFetchingNextInterviewsPage: boolean;
  showApplications: boolean;
  placementCandidateId: string;
  workerTimeZone: string;
  onLoadMore: () => void;
}

export function InterviewsAccordionSection(props: InterviewsAccordionSectionProps) {
  const {
    interviews,
    isLoadingInterviews,
    hasNextPageInterviews,
    isFetchingNextInterviewsPage,
    showApplications,
    placementCandidateId,
    workerTimeZone,
    onLoadMore,
  } = props;

  const [, setShowApplications] = useLocalStorage<boolean>(
    showApplicationsStorageKey,
    showApplications
  );

  const queryParameters = useSearchParams<{ interviews?: string }>();
  const setSearchParams = useSetSearchParams();

  const [expandedInterviews, setExpandedInterviews] = useLocalStorage(
    applicationActiveSectionStorageKey("Booked Interviews"),
    false
  );

  useEffect(() => {
    if (queryParameters.interviews === "true") {
      setExpandedInterviews(true);
      setSearchParams({ interviews: undefined }, { shouldReplace: true });
    }
  }, [queryParameters, setSearchParams, setExpandedInterviews]);

  return (
    <PlacementsAccordionSectionV3
      initialExpanded={expandedInterviews}
      title="Interviews"
      isLoading={isLoadingInterviews}
      hasNextPage={hasNextPageInterviews ?? false}
      isFetchingNextPage={isFetchingNextInterviewsPage}
      emptyText={
        <Stack justifyContent="center" flex={1} alignItems="center" sx={{ pt: 5 }} spacing={2}>
          <Text variant="body1" sx={{ textAlign: "center" }}>
            You have no interviews scheduled yet!{" "}
          </Text>
          <Text>
            Please visit the{" "}
            <TextButton
              onClick={() => {
                setShowApplications(false);
              }}
            >
              Open Staff Jobs tab
            </TextButton>{" "}
            to apply to jobs.
          </Text>
        </Stack>
      }
      totalPlacementsCount={interviews.length}
      placementIds={interviews.map((interview) => interview.id)}
      renderPlacementCard={(interviewId, index) => {
        const interview = interviews?.find((interview) => interview.id === interviewId);
        if (!isDefined(interview)) {
          return null;
        }

        const placementDetailsPath = getPlacementDetailsPath(
          interview.placement?.id ?? "",
          placementCandidateId
        );

        return (
          <InterviewPlacementCard
            interview={{
              start: interview.start,
              end: interview.end,
              status: interview.status,
              scheduleStatus: interview.scheduleStatus,
              isBanExcused: interview.isBanExcused,
            }}
            placement={{
              id: interview.placement?.id ?? "",
              workerTypes: interview.placement?.workerTypes ?? [],
            }}
            distance={interview.workplace?.distance ?? 0}
            workplaceName={interview.workplace?.name ?? ""}
            pageIndex={0}
            cardIndex={index}
            placementCandidateId={placementCandidateId}
            workerTimeZone={workerTimeZone}
            placementDetailsPath={placementDetailsPath}
            source="booked_interviews"
          />
        );
      }}
      onLoadMore={onLoadMore}
    />
  );
}
